import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router';
import { LogIn, Pencil, X, Check, Shield, MessageSquare } from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { useFeatureFlags } from '../contexts/FeatureFlagContext';
import { useAdminChatThreadsAll } from '../hooks/useChatQueries';
import { CostDashboard } from '../components/admin/CostDashboard';
import { formatEur } from '../utils/formatEur';
import api from '../services/api';
import { taggedFetch } from '../services/taggedFetch';
import './AdminPage.css';

interface CostEntry {
  prev_month_eur: number;
  curr_month_eur: number;
}

type CostSortKey = 'prev' | 'curr';

interface KPISnapshot {
  date: string;
  wau: number;
  total_documents: number;
  // #2156: true when at least one tenant's document count failed during
  // collection (missing-not-zero) — total_documents under-counts that day.
  partial: boolean;
}

// #2156: an above-threshold collection day has NO snapshot row, but recharts
// connects neighbouring categorical points straight through — the skipped day
// would render as an interpolated line instead of a gap. Insert null-valued
// placeholder days so the line visibly breaks (connectNulls defaults false).
export interface KpiChartPoint {
  date: string;
  wau: number | null;
  total_documents: number | null;
  partial: boolean;
  // #2924: true when this real day has a null placeholder (or the end of the
  // series) on BOTH sides. recharts needs two adjacent non-null points to draw
  // a segment, so such a day produces no line at all and — with dots
  // suppressed — rendered as nothing despite its data being present.
  isolated: boolean;
}

// #2924: a placeholder nulls both series at once and a real row always carries
// both numbers, so one null check answers "is there anything to draw here?" for
// either line. Off the end of the array counts as missing, which is what makes
// the first point, the final point and a single-row series isolated.
const isKpiPointMissing = (point: KpiChartPoint | undefined): boolean =>
  point === undefined || point.wau === null || point.total_documents === null;

export const fillKpiGaps = (rows: KPISnapshot[]): KpiChartPoint[] => {
  const DAY_MS = 24 * 60 * 60 * 1000;
  const out: KpiChartPoint[] = [];
  rows.forEach((current, i) => {
    out.push({ ...current, isolated: false });
    if (i + 1 < rows.length) {
      const next = new Date(rows[i + 1].date).getTime();
      for (let t = new Date(current.date).getTime() + DAY_MS; t < next; t += DAY_MS) {
        out.push({
          date: new Date(t).toISOString().slice(0, 10),
          wau: null,
          total_documents: null,
          partial: false,
          isolated: false,
        });
      }
    }
  });
  // Second pass: a point's isolation depends on its neighbours, which only
  // exist once the placeholders are in. Placeholders themselves are never
  // isolated — they are the gap, not a day worth marking.
  return out.map((point, i) =>
    isKpiPointMissing(point)
      ? point
      : { ...point, isolated: isKpiPointMissing(out[i - 1]) && isKpiPointMissing(out[i + 1]) },
  );
};

export const KPI_WAU_STROKE = '#2563eb';
export const KPI_DOCS_STROKE = '#16a34a';
// #2156: amber marks a day whose total_documents under-counts.
export const KPI_PARTIAL_FILL = '#f59e0b';

interface KpiDotProps {
  cx?: number;
  cy?: number;
  index?: number;
  payload?: KpiChartPoint;
}

// #2156 + #2924: mark partial snapshot points, and give an isolated day a real
// marker so it is visible even though no line segment can reach it. Both lines
// use this — a bare `dot={false}` is what made 2026-08-09 render blank. recharts
// expects an SVG element back from a dot render fn, so a point that deserves no
// marker returns an invisible r=0 circle, never null. Deliberately NOT always-on
// dots: this chart carries up to 90 points and would smear.
export const makeKpiDotRenderer = (stroke: string) => {
  const renderKpiDot = ({ cx, cy, index, payload }: KpiDotProps) => {
    if (payload?.partial) {
      return (
        <circle
          key={`kpi-dot-${index}`}
          cx={cx}
          cy={cy}
          r={4}
          fill={KPI_PARTIAL_FILL}
          stroke="#ffffff"
          strokeWidth={1}
        />
      );
    }
    if (payload?.isolated) {
      return <circle key={`kpi-dot-${index}`} cx={cx} cy={cy} r={3} fill={stroke} stroke="#ffffff" strokeWidth={1} />;
    }
    return <circle key={`kpi-dot-${index}`} cx={cx} cy={cy} r={0} fill="none" />;
  };
  renderKpiDot.displayName = 'KpiDot';
  return renderKpiDot;
};

const renderWauDot = makeKpiDotRenderer(KPI_WAU_STROKE);
const renderDocsDot = makeKpiDotRenderer(KPI_DOCS_STROKE);

interface User {
  id: string;
  phone: string;
  is_active: boolean;
  has_passkey: boolean;
  last_login_at: string | null;
  // #1220: last_active_at = raw request activity; last_interactive_at =
  // de-noised genuine interaction. The "Last Active" column displays the
  // de-noised value and falls back to the raw one while last_interactive_at
  // is still NULL (forward-only column, no backfill).
  last_active_at: string | null;
  last_interactive_at: string | null;
  created_at: string;
  account_type: string;
  first_name: string | null;
  last_name: string | null;
  farm_name: string | null;
  deleted_at: string | null;
  roles: string[];
}

const KPICharts: React.FC<{ snapshots: KPISnapshot[] }> = ({ snapshots }) => {
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - 90);
  const data = snapshots.filter((s) => new Date(s.date) >= cutoff);
  // #2156: null placeholders for skipped days break the line into a real gap.
  const chartData = fillKpiGaps(data);

  const latestWau = data.length > 0 ? data[data.length - 1].wau : null;
  const latestDocs = data.length > 0 ? data[data.length - 1].total_documents : null;

  const formatDateTick = (value: string) =>
    new Date(value).toLocaleDateString('de-DE', { month: 'short', day: 'numeric' });

  return (
    <div className="kpi-charts-row">
      <div className="kpi-chart-card">
        <div className="kpi-chart-title">Wöchentlich aktive Nutzer (WAU)</div>
        {latestWau !== null && <div className="kpi-latest-value">{latestWau}</div>}
        {data.length === 0 ? (
          <div className="kpi-no-data">Noch keine Daten</div>
        ) : (
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={chartData} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="date" tickFormatter={formatDateTick} tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip
                labelFormatter={(v) => new Date(v as string).toLocaleDateString('de-DE')}
                formatter={(v: number) => [v, 'WAU']}
              />
              <Line type="monotone" dataKey="wau" stroke={KPI_WAU_STROKE} dot={renderWauDot} strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="kpi-chart-card">
        <div className="kpi-chart-title">Dokumente gesamt</div>
        {latestDocs !== null && <div className="kpi-latest-value">{latestDocs.toLocaleString('de-DE')}</div>}
        {data.length === 0 ? (
          <div className="kpi-no-data">Noch keine Daten</div>
        ) : (
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={chartData} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="date" tickFormatter={formatDateTick} tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip
                labelFormatter={(v) => new Date(v as string).toLocaleDateString('de-DE')}
                formatter={(v: number, _name, item: { payload?: KpiChartPoint }) => [
                  `${v.toLocaleString('de-DE')}${item?.payload?.partial ? ' (unvollständig)' : ''}`,
                  'Dokumente',
                ]}
              />
              <Line
                type="monotone"
                dataKey="total_documents"
                stroke={KPI_DOCS_STROKE}
                dot={renderDocsDot}
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

const AdminPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasFlag } = useFeatureFlags();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [documentCounts, setDocumentCounts] = useState<Record<string, number | null>>({});
  const [costSummary, setCostSummary] = useState<Record<string, CostEntry>>({});
  const [sortKey, setSortKey] = useState<CostSortKey | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({ first_name: '', last_name: '', farm_name: '' });
  const [savingEdit, setSavingEdit] = useState(false);
  const [impersonating, setImpersonating] = useState<string | null>(null);
  const [togglingStatus, setTogglingStatus] = useState<string | null>(null);
  const [kpiSnapshots, setKpiSnapshots] = useState<KPISnapshot[]>([]);

  // Unread aggregate for the /admin → /admin/messages link badge.
  // ``useAdminChatThreadsAll`` queries the cross-tenant feed and is
  // invalidated by the shared ``useChatSSE`` hook on every
  // ``chat:message.new`` / ``chat:message.read`` / ``chat:message.deleted``
  // SSE frame — the badge therefore auto-refreshes in near-real-time.
  const adminThreadsQuery = useAdminChatThreadsAll();
  const totalUnread = useMemo(
    () =>
      (adminThreadsQuery.data ?? [])
        // #1680: only human-support threads are real support work. Agent
        // (KI-Hofassistent) conversations owe no human reply and must not
        // inflate the badge. The backend feed already excludes them; this
        // guards stale/legacy payloads that still carry agent rows.
        .filter((row) => row.thread_kind === 'human_support')
        .reduce((sum, row) => sum + (row.unread_count ?? 0), 0),
    [adminThreadsQuery.data],
  );

  const fetchUsers = useCallback(async () => {
    try {
      const response = await taggedFetch('/api/admin/users?limit=500', {
        credentials: 'include',
      });

      if (response.status === 403) {
        setError('Access denied. Admin privileges required.');
        setLoading(false);
        return;
      }

      if (response.status === 429) {
        throw new Error('Too many requests. Please wait a moment and refresh.');
      }

      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }

      const data = await response.json();
      setUsers(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Fetch KPI snapshots for charts
  useEffect(() => {
    const loadKpi = async () => {
      try {
        const res = await taggedFetch('/api/admin/kpi/snapshots', { credentials: 'include' });
        if (res.ok) setKpiSnapshots(await res.json());
      } catch (err) {
        console.error('Failed to load KPI snapshots:', err);
      }
    };
    loadKpi();
  }, []);

  // Fetch all document counts in a single batch request
  useEffect(() => {
    if (users.length === 0) return;
    let cancelled = false;
    const loadDocCounts = async () => {
      try {
        const res = await taggedFetch('/api/admin/users/document-counts', {
          credentials: 'include',
        });
        if (res.ok && !cancelled) {
          setDocumentCounts(await res.json());
        }
      } catch (err) {
        console.error('Failed to load document counts:', err);
      }
    };
    loadDocCounts();
    return () => { cancelled = true; };
  }, [users]);

  // Fetch prev/curr-month EUR cost per user in a single batch request (#400 A9).
  useEffect(() => {
    if (users.length === 0) return;
    let cancelled = false;
    const loadCostSummary = async () => {
      try {
        const res = await taggedFetch('/api/admin/users/cost-summary', {
          credentials: 'include',
        });
        if (res.ok && !cancelled) {
          setCostSummary(await res.json());
        }
      } catch (err) {
        console.error('Failed to load cost summary:', err);
      }
    };
    loadCostSummary();
    return () => { cancelled = true; };
  }, [users]);

  const copyToClipboard = async (userId: string) => {
    try {
      await navigator.clipboard.writeText(userId);
      setCopiedId(userId);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const startEdit = (user: User) => {
    setEditingUserId(user.id);
    setEditForm({
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      farm_name: user.farm_name || '',
    });
  };

  const cancelEdit = () => {
    setEditingUserId(null);
  };

  const saveEdit = async () => {
    if (!editingUserId) return;
    setSavingEdit(true);
    try {
      await api.patch(`/v1/admin/users/${editingUserId}/profile`, editForm);
      // Update local state instead of re-fetching (avoids rate limit cascade from doc counts)
      setUsers(prev => prev.map(u =>
        u.id === editingUserId
          ? { ...u, first_name: editForm.first_name || null, last_name: editForm.last_name || null, farm_name: editForm.farm_name || null }
          : u
      ));
      setEditingUserId(null);
    } catch (err) {
      console.error('Failed to save profile:', err);
    } finally {
      setSavingEdit(false);
    }
  };

  const handleImpersonate = async (userId: string) => {
    setImpersonating(userId);
    try {
      await api.post('/admin/impersonate', { user_id: userId });
      window.location.href = '/documents';
    } catch (err: unknown) {
      // If already impersonating, stop first then retry
      const axiosErr = err as { response?: { status?: number; data?: { detail?: string } } };
      if (axiosErr.response?.status === 400 && axiosErr.response?.data?.detail?.includes('Already impersonating')) {
        try {
          await api.post('/admin/stop-impersonate');
          await api.post('/admin/impersonate', { user_id: userId });
          window.location.href = '/documents';
          return;
        } catch (retryErr) {
          console.error('Impersonation retry failed:', retryErr);
        }
      } else {
        console.error('Impersonation failed:', err);
      }
      setImpersonating(null);
    }
  };

  const handleToggleStatus = async (user: User) => {
    if (user.deleted_at || user.roles.includes('admin')) return;
    setTogglingStatus(user.id);
    try {
      const res = await api.patch(`/admin/users/${user.id}/status`);
      setUsers(prev => prev.map(u =>
        u.id === user.id ? { ...u, is_active: res.data.is_active } : u
      ));
    } catch (err) {
      console.error('Failed to toggle status:', err);
    } finally {
      setTogglingStatus(null);
    }
  };

  const userStatus = (user: User): 'active' | 'inactive' | 'deleted' => {
    if (user.deleted_at) return 'deleted';
    return user.is_active ? 'active' : 'inactive';
  };

  const fullName = (user: User) => {
    const parts = [user.first_name, user.last_name].filter(Boolean);
    return parts.length > 0 ? parts.join(' ') : '—';
  };

  const filteredUsers = users.filter(user => {
    const term = searchTerm.toLowerCase();
    return (
      user.phone.includes(searchTerm) ||
      user.id.toLowerCase().includes(term) ||
      (user.first_name && user.first_name.toLowerCase().includes(term)) ||
      (user.last_name && user.last_name.toLowerCase().includes(term)) ||
      (user.farm_name && user.farm_name.toLowerCase().includes(term))
    );
  });

  const toggleSort = (key: CostSortKey) => {
    if (sortKey === key) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  // Sort by a cost column when one is selected; users without a loaded cost
  // figure sort last in both directions (cost arrives async — avoids row jitter).
  const sortedUsers = useMemo(() => {
    if (!sortKey) return filteredUsers;
    const costOf = (u: User): number | null => {
      const entry = costSummary[u.id];
      if (!entry) return null;
      return sortKey === 'prev' ? entry.prev_month_eur : entry.curr_month_eur;
    };
    return [...filteredUsers].sort((a, b) => {
      const av = costOf(a);
      const bv = costOf(b);
      if (av === null && bv === null) return 0;
      if (av === null) return 1;
      if (bv === null) return -1;
      return sortDir === 'asc' ? av - bv : bv - av;
    });
  }, [filteredUsers, sortKey, sortDir, costSummary]);

  const sortIndicator = (key: CostSortKey) => (sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '');

  if (loading) {
    return <div className="admin-loading">Loading users...</div>;
  }

  if (error) {
    const isAccessDenied = error.includes('Access denied');
    return (
      <div className="admin-error">
        <h2>{isAccessDenied ? 'Access Denied' : 'Error'}</h2>
        <p>{error}</p>
        <button onClick={() => isAccessDenied ? navigate('/') : window.location.reload()}>
          {isAccessDenied ? 'Go to Home' : 'Retry'}
        </button>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h1>{t('admin.title', 'User Management')}</h1>
        <p className="admin-subtitle">
          {t('admin.subtitle', 'Manage users, profiles, and impersonation')}
        </p>
      </div>

      <div className="admin-tools">
        <Link to="/admin/messages" className="admin-tool-button admin-messages-link">
          <MessageSquare size={16} aria-hidden />
          <span>{t('admin.messagesLink', 'Messages')}</span>
          {totalUnread > 0 && (
            <span
              className="admin-messages-link__badge"
              aria-label={t('admin.unreadBadgeAria', '{{count}} unread', { count: totalUnread })}
            >
              {totalUnread > 99 ? '99+' : totalUnread}
            </span>
          )}
        </Link>
        <button
          className="kibana-button"
          onClick={() => window.open('/kibana/app/dashboards', '_blank')}
        >
          Open Kibana Dashboard
        </button>
        {hasFlag('admin.feature_flags') && (
          <Link to="/admin/features" className="kibana-button">
            Feature Flags
          </Link>
        )}
      </div>

      <KPICharts snapshots={kpiSnapshots} />

      <CostDashboard />

      <div className="user-lookup-card">
        <div className="search-box">
          <input
            type="text"
            placeholder={t('admin.searchPlaceholder', 'Search by name, farm, phone, or ID...')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="users-table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th>{t('admin.lastActive', 'Last Active')}</th>
                <th>{t('admin.userTable.farmName', 'Farm Name')}</th>
                <th>{t('admin.userTable.fullName', 'Full Name')}</th>
                <th>{t('admin.userTable.phone', 'Phone')}</th>
                <th>{t('admin.userTable.type', 'Type')}</th>
                <th>{t('admin.userTable.docs', 'Docs')}</th>
                <th className="sortable-col" onClick={() => toggleSort('prev')} title={t('admin.userTable.sortPrev', 'Sort by previous month')}>
                  {t('admin.userTable.prevMonth', 'Prev month (€)')}{sortIndicator('prev')}
                </th>
                <th className="sortable-col" onClick={() => toggleSort('curr')} title={t('admin.userTable.sortCurr', 'Sort by current month')}>
                  {t('admin.userTable.currMonth', 'Curr month (€)')}{sortIndicator('curr')}
                </th>
                <th>{t('admin.userTable.status', 'Status')}</th>
                <th>{t('admin.userTable.actions', 'Actions')}</th>
              </tr>
            </thead>
            <tbody>
              {sortedUsers.map((user) => (
                <React.Fragment key={user.id}>
                  <tr className={impersonating === user.id ? 'row-impersonating' : ''}>
                    {/* #1220 de-noised signal, falling back to raw last_active_at:
                        last_interactive_at is forward-only (no backfill), so
                        pre-cutover users would otherwise all render "—". */}
                    <td className="date-cell last-active-cell">
                      {formatDate(user.last_interactive_at ?? user.last_active_at)}
                    </td>
                    <td>{user.farm_name || '—'}</td>
                    <td>{fullName(user)}</td>
                    <td className="phone-cell">{user.phone}</td>
                    <td>
                      <span className={`type-badge type-${user.account_type}`}>
                        {user.account_type === 'farmer'
                          ? t('admin.userTable.accountFarmer', 'Farmer')
                          : t('admin.userTable.accountEmployee', 'Employee')}
                      </span>
                      {user.roles.includes('admin') && (
                        <span title="Admin"><Shield size={12} className="admin-icon" /></span>
                      )}
                    </td>
                    <td className="doc-count-cell">
                      {documentCounts[user.id] !== undefined
                        ? (documentCounts[user.id] ?? '—')
                        : <span className="loading-dots">...</span>
                      }
                    </td>
                    <td className="cost-cell">
                      {costSummary[user.id] ? formatEur(costSummary[user.id].prev_month_eur) : '—'}
                    </td>
                    <td className="cost-cell">
                      {costSummary[user.id] ? formatEur(costSummary[user.id].curr_month_eur) : '—'}
                    </td>
                    <td>
                      <button
                        className={`status-badge ${userStatus(user)}`}
                        onClick={() => handleToggleStatus(user)}
                        disabled={!!user.deleted_at || user.roles.includes('admin') || togglingStatus === user.id}
                        title={
                          user.deleted_at
                            ? t('admin.userTable.selfDeleted', 'Self-deleted account')
                            : user.roles.includes('admin')
                              ? t('admin.userTable.adminLocked', 'Cannot change admin status')
                              : user.is_active
                                ? t('admin.userTable.clickToBlock', 'Click to block')
                                : t('admin.userTable.clickToUnblock', 'Click to unblock')
                        }
                      >
                        {togglingStatus === user.id
                          ? '...'
                          : userStatus(user) === 'deleted'
                            ? t('admin.userTable.deleted', 'Deleted')
                            : user.is_active
                              ? t('admin.userTable.active', 'Active')
                              : t('admin.userTable.inactive', 'Inactive')}
                      </button>
                    </td>
                    <td className="actions-cell">
                      <button
                        className="action-btn action-edit"
                        onClick={() => startEdit(user)}
                        title={t('admin.userTable.editProfile', 'Edit profile')}
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        className="action-btn action-copy"
                        onClick={() => copyToClipboard(user.id)}
                        title={t('admin.userTable.copyId', 'Copy User ID')}
                      >
                        {copiedId === user.id ? '✓' : 'ID'}
                      </button>
                      <button
                        className="action-btn action-impersonate"
                        onClick={() => handleImpersonate(user.id)}
                        disabled={impersonating === user.id}
                        title={t('admin.userTable.impersonate', 'Impersonate user')}
                      >
                        <LogIn size={14} />
                      </button>
                    </td>
                  </tr>
                  {editingUserId === user.id && (
                    <tr className="edit-row">
                      <td colSpan={10}>
                        <div className="edit-form">
                          <div className="edit-field">
                            <label>First Name</label>
                            <input
                              value={editForm.first_name}
                              onChange={e => setEditForm(f => ({ ...f, first_name: e.target.value }))}
                              placeholder="First name"
                            />
                          </div>
                          <div className="edit-field">
                            <label>Last Name</label>
                            <input
                              value={editForm.last_name}
                              onChange={e => setEditForm(f => ({ ...f, last_name: e.target.value }))}
                              placeholder="Last name"
                            />
                          </div>
                          <div className="edit-field">
                            <label>Farm Name</label>
                            <input
                              value={editForm.farm_name}
                              onChange={e => setEditForm(f => ({ ...f, farm_name: e.target.value }))}
                              placeholder="Farm name"
                            />
                          </div>
                          <div className="edit-actions">
                            <button className="action-btn action-save" onClick={saveEdit} disabled={savingEdit}>
                              <Check size={14} /> {savingEdit ? 'Saving...' : 'Save'}
                            </button>
                            <button className="action-btn action-cancel" onClick={cancelEdit}>
                              <X size={14} /> Cancel
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>

          {filteredUsers.length === 0 && (
            <div className="no-results">
              {searchTerm ? 'No users found matching your search' : 'No users found'}
            </div>
          )}
        </div>

        <div className="users-count">
          {filteredUsers.length} of {users.length} users
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
